<script>
  import FormInputWithLabel from './FormInputWithLabel.svelte';
  import SearchSelect from './SearchSelect.svelte';
  import FormLabel from './FormLabel.svelte';
  import FloatInput from './FloatInput.svelte';
  import IntegerInput from './IntegerInput.svelte';
  import ButtonLabel from './ButtonLabel.svelte';
  import Alert from './Alert.svelte';
  import DraggableList from './DraggableList.svelte';
  import DraggableItem from './DraggableItem.svelte';
  import Dialog from './Dialog.svelte';
  import t from './i18n.svelte.js';
  import { nanoid } from './util.js';
  import Switch from './Switch.svelte';
  import Icon from './Icon.svelte';
  import TextInput from './TextInput.svelte';

  let { 
    /** @type {Array<{id: string, type: string, label: string, placeholder?: string, required: boolean, options?: string[], validate_value?: (value: any) => {valid: boolean, error?: string}}>} */
    fields = [],
    /** @type {boolean} Whether the form is in editable mode (admin configuration) */
    editable = false,
    /** @type {Record<string, any>} Current field values, bound to parent component */
    values = $bindable({}),
    /** @type {{custom_form_fields?: Array<any>} | null} Site settings object containing custom form fields configuration */
    site_settings = null,
    /** @type {Record<string, string>} Validation errors for fields */
    errors = {},
    /** @type {boolean} Whether to enforce required field validation, e.g. admins can edit users without enforcing required fields */
    enforce_required = true,
    /** @type {string}  */
    class: css_class = '',
    /** @type {Record<string, any>} Additional props to spread to the component */
    ...rest 
  } = $props();

  // Field type options for adding new fields
  const FIELD_TYPES = [
    { title: t('field_type_text'), value: 'text', icon: 'text-input', has_placeholder: true },
    { title: t('field_type_longtext'), value: 'longtext', icon: 'description', has_placeholder: true },
    { title: t('field_type_float_number'), value: 'float_number', icon: 'decimal', has_placeholder: false },
    { title: t('field_type_integer_number'), value: 'integer_number', icon: 'integer', has_placeholder: false },
    { title: t('field_type_checkbox'), value: 'checkbox', icon: 'check-inverted', has_placeholder: false },
    { title: t('field_type_select'), value: 'select', icon: 'list', has_placeholder: true },
    { title: t('field_type_phone'), value: 'phone', icon: 'phone', has_placeholder: true },
    { title: t('field_type_weblink'), value: 'weblink', icon: 'link', has_placeholder: true },
    // Template shortcuts that create select fields with pre-filled options
    { title: t('countries'), value: 'template_countries', icon: 'globe', has_placeholder: true, is_template: true },
    { title: t('languages'), value: 'template_languages', icon: 'text', has_placeholder: true, is_template: true },
    { title: t('common_sources'), value: 'template_common_sources', icon: 'search', has_placeholder: true, is_template: true },
    { title: t('gender_pronouns'), value: 'template_gender_pronouns', icon: 'calm-face', has_placeholder: true, is_template: true },
  ];

  // Helper function to get field type config
  function get_field_type_config(type) {
    return FIELD_TYPES.find(ft => ft.value === type) || { icon: 'bullet', has_placeholder: false };
  }

  // Local validation errors for each field
  let field_errors = $state({});
  
  // Dialog state for editing select options
  let options_dialog_open = $state(false);
  let editing_field_index = $state(-1);
  let dialog_options = $state([]);

  // Add field dropdown selection
  let selected_field_type = $state('');

  /**
   * Validates a specific field value
   */
  function validate_field(field) {
    if (!field || editable) return true;
    
    const value = values[field.id];

    // When not enforcing required, empty values are acceptable
    if (!enforce_required && (value == null || String(value).trim() === '')) {
      field_errors[field.id] = '';
      return true;
    }

    const validation_result = field.validate_value(value);

    // Ignore required-only errors if enforce_required is false
    if (!enforce_required && !validation_result.valid) {
      const required_msg = t('field_is_required', { field: field.label || field.id });
      if (validation_result.error === required_msg) {
        field_errors[field.id] = '';
        return true;
      }
    }
    
    if (validation_result.valid) {
      field_errors[field.id] = '';
    } else {
      field_errors[field.id] = validation_result.error;
    }
    
    return validation_result.valid;
  }

  function get_input_type(field_type) {
    switch (field_type) {
      case 'float_number':
      case 'integer_number':
        return 'number';
      case 'phone':
        return 'tel';
      case 'weblink':
        return 'url';
      default:
        return 'text';
    }
  }

  // Admin editing functions
  async function add_field_by_type() {
    if (selected_field_type && editable && site_settings) {
      const field_id = nanoid();
      
      // Check if this is a template type
      if (selected_field_type.startsWith('template_')) {
        const template_type = selected_field_type.replace('template_', '');
        
        // Pre-fill template data
        try {
          let template_data = [];
          let field_label = '';
          
          switch (template_type) {
            case 'countries':
              const { COUNTRIES } = await import('./data/countries.js');
              template_data = COUNTRIES.map(country => country.title + ' (' + country.value + ')');
              field_label = t('countries');
              break;
            case 'languages':
              const { LANGUAGES } = await import('./data/languages.js');
              template_data = LANGUAGES.map(lang => lang.title + ' (' + lang.value + ')');
              field_label = t('languages');
              break;
            case 'common_sources':
              const { COMMON_SOURCES } = await import('./data/common_sources.js');
              template_data = COMMON_SOURCES.map(source => source.title);
              field_label = t('common_sources');
              break;
            case 'gender_pronouns':
              const { GENDER_PRONOUNS } = await import('./data/gender_pronouns.js');
              template_data = GENDER_PRONOUNS.map(gp => gp.title);
              field_label = t('gender_pronouns');
              break;
          }
          
          // Create select field with pre-filled data
          site_settings.add_custom_form_field({
            id: field_id,
            label: field_label,
            placeholder: '',
            type: 'select',
            required: false,
            options: template_data
          });
        } catch (error) {
          console.error('Failed to load template data:', error);
          // Fallback: create empty select field
          site_settings.add_custom_form_field({
            id: field_id,
            label: '',
            placeholder: '',
            type: 'select',
            required: false,
            options: []
          });
        }
      } else {
        // Regular field type
        site_settings.add_custom_form_field({
          id: field_id,
          label: '',
          placeholder: '',
          type: selected_field_type,
          required: false,
          options: []
        });
      }
      
      selected_field_type = ''; // Reset selection
    }
  }

  function remove_field(index) {
    if (editable && site_settings) {
      const confirmed = confirm(t('confirm_delete_field_message'));
      if (confirmed) {
        site_settings.remove_custom_form_field(index);
      }
    }
  }

  function toggle_required(field) {
    if (editable) {
      field.required = !field.required;
    }
  }



  function open_options_dialog(field_index) {
    editing_field_index = field_index;
    const current_fields = editable && site_settings ? site_settings.custom_form_fields : fields;
    dialog_options = [...current_fields[field_index].options];
    options_dialog_open = true;
  }

  function save_options() {
    if (editing_field_index >= 0) {
      const current_fields = editable && site_settings ? site_settings.custom_form_fields : fields;
      current_fields[editing_field_index].options = [...dialog_options];
    }
    options_dialog_open = false;
    editing_field_index = -1;
  }

  function cancel_options() {
    options_dialog_open = false;
    editing_field_index = -1;
    dialog_options = [];
  }

  function add_dialog_option() {
    dialog_options = [...dialog_options, ''];
  }

  function remove_dialog_option(index) {
    dialog_options = dialog_options.filter((_, i) => i !== index);
  }

  // Removed dead template-prefill helpers (replaced by add_field_by_type templates)

  // Reactive validation for form mode
  $effect(() => {
    if (!editable) {
      const current_fields = editable && site_settings ? site_settings.custom_form_fields : fields;
      // Coerce values for proper component behavior and submission
      for (const field of current_fields) {
        const raw = values[field.id];
        if (raw !== undefined && raw !== null) {
          if (field.type === 'checkbox') {
            if (raw === 'true') values[field.id] = true;
            if (raw === 'false') values[field.id] = false;
          } else if (field.type === 'float_number' && typeof raw === 'string' && raw.trim() !== '') {
            const num = parseFloat(raw);
            if (!Number.isNaN(num)) values[field.id] = num;
          } else if (field.type === 'integer_number' && typeof raw === 'string' && raw.trim() !== '') {
            const num = parseInt(raw, 10);
            if (!Number.isNaN(num)) values[field.id] = num;
          }
        }
      }
      for (const field of current_fields) {
        const value = values[field.id];
        if (value !== undefined && value !== null && value.toString().trim() !== '') {
          validate_field(field);
        } else if (!enforce_required || !field.required) {
          field_errors[field.id] = '';
        }
      }
    }
  });

  let field_list = $derived.by(() => {
    const current_fields = editable && site_settings ? site_settings.custom_form_fields : fields;
    return current_fields.map((field, index) => ({ field, index }));
  });

  // Watch for field type selection
  $effect(() => {
    if (selected_field_type) {
      add_field_by_type();
    }
  });
</script>

{#if editable}
  <!-- Header for admin mode -->
  <div class='flex-row gap-x g-4 items-center'>
    <SearchSelect
      bind:value={selected_field_type}
      options={FIELD_TYPES}
      placeholder={t('add_field')}
      allow_null={true}
      start_icon='plus'
    />
  </div>
  <p class='text-control'>{t('configure_custom_fields_description')}</p>
{/if}

<div class="custom-fields-form {css_class}" {...rest}>
  {#if (editable && site_settings ? site_settings.custom_form_fields : fields).length > 0}
    {#if editable}
      <!-- Draggable list for admin -->
      <DraggableList bind:list={site_settings.custom_form_fields} direction='column'>
        {#each field_list as { field, index }}
          <DraggableItem {index} is_editing={true}>
            {@render field_row(field, index)}
          </DraggableItem>
        {/each}
      </DraggableList>
    {:else}
      <!-- Simple list for user forms -->
      <div class='flex-column gap-y g-4'>
        {#each field_list as { field, index }}
          <div class='flex-column gap-y g-2'>
            {@render field_row(field, index)}
          </div>
        {/each}
      </div>
    {/if}
  {:else if editable}
    <p class='text-caption'>{t('no_custom_fields_configured')}</p>
  {/if}
</div>

<!-- Options Dialog -->
{#if options_dialog_open}
  <Dialog onclose={() => options_dialog_open = false} class="wide options-dialog">
    <main>
      <header class='flex-wrap items-start gap-x gap-y g-6 justify-between px py p-6'>
        <div class='flex-column'>
          <h3 class='text-heading3'>{t('custom_field_options')}</h3>
        </div>
        <button onclick={() => options_dialog_open = false} title={t('close')}>
          <ButtonLabel variant='brand ghostly' start_icon='close' />
        </button>
      </header>
      
      <section class='flex-column gap-y g-4 px py p-6'>
        <div class='flex-row gap-x g-2 items-center flex-wrap'>
          <button onclick={add_dialog_option}>
            <ButtonLabel start_icon='plus' variant='encased' align='start'>{t('add_option')}</ButtonLabel>
          </button>
        </div>
        
        {#each dialog_options as option, option_index}
          <div class='flex-row gap-x g-2 items-center'>
            <FormInputWithLabel
              type='text'
              bind:value={dialog_options[option_index]}
              placeholder={`Option ${option_index + 1}`}
              class='full-width'
              orientation='column'
            />
            <button onclick={() => remove_dialog_option(option_index)} title={t('delete')}>
              <ButtonLabel start_icon='trash' variant='outlined' />
            </button>
          </div>
        {/each}
      </section>
      
              <footer class='flex-row gap-x g-2 items-center justify-end px py p-6'>
          <button onclick={cancel_options}>
            <ButtonLabel>{t('cancel')}</ButtonLabel>
          </button>
          <button onclick={save_options}>
            <ButtonLabel variant='outlined'>{t('save')}</ButtonLabel>
          </button>
        </footer>
    </main>
  </Dialog>
{/if}

{#snippet field_row(field, index)}
  {@const inline_checkbox = !editable && field.type === 'checkbox'}
  <!--
    A user-facing checkbox is laid out inline — box first, label beside it — rather than through
    the usual `.form-row` grid. That grid is `var(--label-width) 1fr`, which puts the label in
    column 1 and the control in column 2: correct for a text input, wrong for a checkbox, where it
    strands the box in the middle of the row with its label detached and left-aligned above/beside
    it. Every other consent checkbox on the signup form (the Datenschutz one) is already box-then-
    label, so this also makes custom fields match what sits directly beneath them.
  -->
  <div class='custom-field-row gap-x py p-3 full-width
              {editable ? 'flex-row g-6 items-start'
                        : inline_checkbox ? 'flex-row g-3 items-center'
                                          : 'form-row g-1 items-start'}'>
    <!-- Label section — for an inline checkbox the label travels with the input instead -->
    {#if !inline_checkbox}
    <div class='flex-column gap-y g-1 {editable ? 'flex-grow' : ''} full-height'>
      {#if editable}
        <div class="flex-row gap-x g-2 items-center">
          <Icon name={get_field_type_config(field.type).icon} />
          <FormLabel for={field.id} class='py p-0 no-min-height'>
            {t('custom_field_label')}
          </FormLabel>
        </div>
        <TextInput
          id={field.id}
          type='text'          
          bind:value={field.label}
          required={true}
          orientation='column'
          variant='canvas outlined'
        />
      {:else}
        <FormLabel for={field.id}>
          {field.label}{#if field.required}*{/if}
        </FormLabel>
      {/if}
    </div>
    {/if}

    <!-- Input section -->
    <div class='{inline_checkbox ? 'flex-row items-center gap-x g-3 full-width' : `flex-column gap-y g-2 ${editable ? '' : 'full-width'}`}'>
      {#if editable}
        {#if get_field_type_config(field.type).has_placeholder}
          <FormInputWithLabel
            type='text'
            label={t('custom_field_placeholder')}
            bind:value={field.placeholder}
            orientation='column'
            variant='canvas outlined'
          />
        {/if}
      {:else}
        <!-- User input based on field type -->
        {#if field.type === 'checkbox'}
          <div class='flex-row gap-x g-3 items-center'>
            <input 
              type="checkbox" 
              id={field.id}
              checked={values[field.id] === true}
              onchange={(e) => {
                values[field.id] = e.target.checked;
                validate_field(field);
              }}
              required={enforce_required && field.required}
              onblur={() => validate_field(field)}
            />
            <!-- The label sits here rather than in the label section above, so it reads as the
                 thing being ticked. `for` still points at the input, so clicking it toggles.
                 `px p-0` matters: FormLabel only drops its inline padding inside a `.flex-column`
                 (see its `:global(.flex-column > .form-label)` rule), and this is a flex ROW, so
                 without it the label carries an extra indent on top of the gap. -->
            <FormLabel for={field.id} class='px py p-0 no-min-height'>
              {field.label}{#if field.required}*{/if}
            </FormLabel>
            <!-- This is the hidden input that gets submitted to the server -->
            <input
              type='hidden'
              name={`custom_field_${field.id}`}
              value={values[field.id] === true ? 'true' : 'false'}
            />
          </div>
          
        {:else if field.type === 'select'}
          <SearchSelect
            name={`custom_field_${field.id}`}
            bind:value={values[field.id]}
            options={field.options.map(opt => ({ title: opt, value: opt, type: 'string' }))}
            placeholder={field.placeholder || t('select_an_option')}
            allow_null={!enforce_required || !field.required}
            on_blur={() => validate_field(field)}
          />
          
        {:else if field.type === 'float_number'}
          <FloatInput
            id={field.id}
            bind:value={values[field.id]}
            placeholder={field.placeholder}
            required={enforce_required && field.required}
            variant='canvas outlined'
            onblur={() => validate_field(field)}
          />
          <!-- Hidden input for form submission -->
          <input type='hidden' name={`custom_field_${field.id}`} value={values[field.id] ?? ''} />
          
        {:else if field.type === 'integer_number'}
          <IntegerInput
            id={field.id}
            bind:value={values[field.id]}
            placeholder={field.placeholder}
            required={enforce_required && field.required}
            variant='canvas outlined'
            onblur={() => validate_field(field)}
          />
          <!-- Hidden input for form submission -->
          <input type='hidden' name={`custom_field_${field.id}`} value={values[field.id] ?? ''} />
          
        {:else}
          <FormInputWithLabel
            type={get_input_type(field.type)}
            id={field.id}
            name={`custom_field_${field.id}`}
            bind:value={values[field.id]}
            placeholder={field.placeholder}
            required={enforce_required && field.required}
            multiline={field.type === 'longtext' ? true : false}
            orientation='column'
            variant='canvas outlined'
            onblur={() => validate_field(field)}
          />
        {/if}
        
        {#if field_errors[field.id] || (errors && errors[field.id])}
          <Alert variant='error' dismissible={true}>{field_errors[field.id] || errors[field.id]}</Alert>
        {/if}
      {/if}
    </div>

    <!-- Admin controls -->
    {#if editable}
      <!-- Required toggle -->
      <div class='flex-column gap-y g-1'>
        <FormLabel for={`required_${field.id}`}>{t('custom_field_required')}</FormLabel>
        <Switch
          id={`required_${field.id}`}
          value={field.required}
          toggle={() => toggle_required(field)}
          variant='brand ghostly text-compact'
        />
      </div>        

      <!-- Options (only for select fields) -->
      {#if field.type === 'select'}
        <div class='flex-column gap-y g-1'>
          <FormLabel for='options_trigger'>{t('custom_field_options')}</FormLabel>
          <button id='options_trigger' onclick={() => open_options_dialog(index)}>
            <ButtonLabel start_icon='pencil' variant='outlined'>{t('edit')}</ButtonLabel>
          </button>
        </div>
      {/if}

      <!-- Remove field -->
      <button 
        onclick={() => remove_field(index)} 
        title={t('delete_field')}
        class='self-end ms m-auto'
      >
        <ButtonLabel start_icon='trash' variant='outlined' />
      </button>
    {/if}
  </div>
{/snippet}

<style lang='postcss'>
  :global(.options-dialog) main {
    max-height: inherit;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
    grid-template-areas:
      "header"
      "section"
      "footer";
  }
  
  :global(.options-dialog) header {
    grid-area: header;
    border-bottom: 1px solid var(--color-default-stroke);
  }
  
  :global(.options-dialog) section {
    grid-area: section;
    overflow-y: auto;
  }
  
  :global(.options-dialog) footer {
    grid-area: footer;
    border-top: 1px solid var(--color-default-stroke);
  }
  
  :global(.options-dialog) {
    max-height: min(var(--dialog-max-height), 500px);
  }

  .custom-fields-form {
    container-type: inline-size;
    container-name: custom-fields-form;
  }
  .custom-field-row {
    @container custom-fields-form (max-width: 400px) {
      display: flex;
      flex-direction: column;
    }
  }
</style>